import {
  asksForAffiliation,
  type IntakeFieldName,
} from "../IntakeForm/MarketingIntakeForm";
import type { IntakeQuestion } from "../IntakeForm/intakeQuestions";

/**
 * What a form asks, broken into the steps somebody answers it in.
 *
 * A step is one screen. Which screen a question lands on is configured per form
 * rather than decided here, because the three audiences are asked different
 * numbers of things and the design gives each its own shape: the funder answers
 * over five screens, the organization over three, a participant over one.
 */
export type IntakeStep =
  | { kind: "day"; label: string }
  | { kind: "time"; label: string }
  | { kind: "sessions"; label: string }
  | {
      kind: "contact";
      label: string;
      fields: IntakeFieldName[];
      /** Questions configured to be asked alongside the contact fields */
      extra: IntakeQuestion[];
      /** Whether organization and role are answered through the paired selects */
      affiliation: boolean;
      /**
       * Whether the email address and its confirmation are asked here. Every
       * form asks for them, on its first contact screen; a phone's second
       * contact screen, when the contact screen is split, does not.
       */
      asksEmail: boolean;
    }
  | { kind: "questions"; label: string; fields: IntakeQuestion[] };

export type StepPlan = {
  steps: IntakeStep[];
  /** The wording on the button that completes the form */
  finalLabel: string;
};

/** The built-in fields, in the order the design asks for them. */
const CONTACT_ORDER: IntakeFieldName[] = [
  "first_name",
  "last_name",
  "phone_number",
  "organization_name",
  "self_reported_role",
  "message",
];

/**
 * Build the steps for one configured form.
 *
 * Booking comes first, because a meeting with no time left is not worth
 * collecting details for; then the contact fields; then the form's own
 * questions, grouped by the step each is configured on.
 *
 * Step 1 is the contact screen itself, so a question configured there is asked
 * alongside the name and email rather than on a screen of its own. The funder
 * form's website field is the example: the design puts it under Email
 * Confirmation, not on a page by itself.
 */
export function planSteps({
  requiredFields,
  optionalFields,
  extraFields,
  booksAMeeting,
  bookingStepStyle,
  finalLabel,
  fieldLabels,
  splitContact = false,
}: {
  requiredFields: IntakeFieldName[];
  optionalFields: IntakeFieldName[];
  /** Wording overrides, which decide how the organization is asked for */
  fieldLabels?: Record<string, string>;
  extraFields: IntakeQuestion[];
  booksAMeeting: boolean;
  bookingStepStyle?: string;
  finalLabel?: string;
  /**
   * Split the contact screen in two: the name and email first, then the
   * rest of what it asks. A phone is given it this way, since the whole
   * screen is taller than a phone shows; a wider screen asks it all at once,
   * as the design draws it.
   */
  splitContact?: boolean;
}): StepPlan {
  const steps: IntakeStep[] = [];

  if (booksAMeeting) {
    if (bookingStepStyle === "sessions") {
      steps.push({ kind: "sessions", label: "Choose a session that works for you" });
    } else {
      steps.push({ kind: "day", label: "Choose a day that works for you" });
      steps.push({ kind: "time", label: "Choose a time." });
    }
  }

  const byStep = new Map<number, IntakeQuestion[]>();
  extraFields.forEach((field) => {
    const step = field.step ?? 1;
    byStep.set(step, [...(byStep.get(step) ?? []), field]);
  });

  const shown = new Set<IntakeFieldName>([...requiredFields, ...optionalFields]);
  const fields = CONTACT_ORDER.filter((field) => shown.has(field));
  const extra = byStep.get(1) ?? [];
  const affiliation = asksForAffiliation([...shown], fieldLabels);
  const names: IntakeFieldName[] = fields.filter((field) => field === "first_name" || field === "last_name");
  const rest = fields.filter((field) => !names.includes(field));
  if (splitContact && (rest.length > 0 || extra.length > 0)) {
    steps.push({ kind: "contact", label: "Contact information", fields: names, extra: [], affiliation: false, asksEmail: true });
    steps.push({ kind: "contact", label: "Contact information", fields: rest, extra, affiliation, asksEmail: false });
  } else {
    steps.push({ kind: "contact", label: "Contact information", fields, extra, affiliation, asksEmail: true });
  }

  [...byStep.keys()]
    .filter((step) => step > 1)
    .sort((a, b) => a - b)
    .forEach((step) => {
      steps.push({
        kind: "questions",
        label: `Step ${step}`,
        fields: byStep.get(step) ?? [],
      });
    });

  return {
    steps,
    finalLabel:
      finalLabel || (booksAMeeting ? "Book Meeting" : "Submit"),
  };
}

/** The form fields a step is responsible for, for validating one screen at a time. */
export function fieldNamesForStep(step: IntakeStep): string[] {
  if (step.kind === "contact") {
    const names: string[] = step.asksEmail ? ["email", "confirmEmail"] : [];
    step.fields.forEach((field) => {
      if (field === "first_name") names.push("firstName");
      if (field === "last_name") names.push("lastName");
      if (field === "phone_number") names.push("phoneNumber", "confirmPhoneNumber");
      // The paired selects answer both of these as one field, added below.
      if (field === "organization_name" && !step.affiliation) names.push("organizationName");
      if (field === "self_reported_role" && !step.affiliation) names.push("selfReportedRole");
      if (field === "message") names.push("message");
    });
    if (step.affiliation) names.push("affiliations");
    step.extra.forEach((field) => names.push(`answers.${field.name}`));
    return names;
  }
  if (step.kind === "questions") {
    return step.fields.map((field) => `answers.${field.name}`);
  }
  return [];
}
